"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

import { BOTON_SECUNDARIO } from "@/components/shared/estilos";
import { urlGoogleInicio } from "@/lib/acceso";

import IconoGoogle from "./IconoGoogle";
import Spinner from "./Spinner";

// El secundario de siempre con el texto del botón de Google: más grande, más
// negro y en seminegrita. Se sustituye en la cadena y no se añade detrás
// porque en la hoja de estilos `text-sm` va después de `text-base` y ganaría.
const CLASES = [
  BOTON_SECUNDARIO.replace("text-sm font-medium text-zinc-700", "text-base font-semibold text-zinc-900"),
  // A todo lo ancho de la tarjeta, que fija el ancho; en móvil el icono se
  // arrima un poco más al texto.
  "w-full gap-2 sm:gap-3",
].join(" ");

interface BotonGoogleProps {
  /** Ruta propia a la que volver al entrar; el backend la valida otra vez. */
  siguiente?: string | null;
  idioma?: string;
  /** País de la pantalla de acceso (spec 022 §21.4). */
  pais?: string | null;
  /** El token de `/start?invite=` (030-lite). */
  invitacion?: string | null;
}

/**
 * «Continuar con Google». Es una navegación de página entera al inicio del
 * OAuth (spec 022 §8.3), no un fetch: tras el clic el botón queda cargando
 * porque la página se va. Si se vuelve con «Atrás», el navegador puede
 * restaurar la página congelada (bfcache) con el botón aún cargando y
 * deshabilitado: `pageshow` con `persisted` lo devuelve a su estado.
 */
export default function BotonGoogle({ siguiente, idioma, pais, invitacion }: BotonGoogleProps) {
  const t = useTranslations("auth.acceso");
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    function alVolver(evento: PageTransitionEvent): void {
      if (evento.persisted) setCargando(false);
    }
    window.addEventListener("pageshow", alVolver);
    return () => window.removeEventListener("pageshow", alVolver);
  }, []);

  function ir(): void {
    setCargando(true);
    window.location.assign(
      urlGoogleInicio({
        ...(siguiente ? { siguiente } : {}),
        ...(idioma ? { idioma } : {}),
        ...(pais ? { pais } : {}),
        ...(invitacion ? { invitacion } : {}),
      })
    );
  }

  return (
    <button type="button" onClick={ir} disabled={cargando} aria-busy={cargando} className={CLASES}>
      {cargando ? <Spinner className="h-5 w-5" /> : <IconoGoogle tamano={20} />}
      <span>{cargando ? t("googleCargando") : t("google")}</span>
    </button>
  );
}
